/**
 * The old Work addresses, kept resolving. Work's index became Waiting on you
 * and a ticket opens in its Team's queue (`/desks/:deskId?item=:id`), but
 * Slack, Front, the bell and every pasted link still carry `/work?workItem=`,
 * `/work/:id` and `/work/:id/receipt`. Each one reads the ticket to learn its
 * Team, then lands there.
 */
import { useEffect, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import type { WorkItem } from "@yagni/shared";
import { toast } from "../ui/toast.js";
import { captureUiError } from "../sentry.js";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The ticket behind an id or a human identifier (`YAG-42`), or null when it cannot be read. */
export async function readWorkItemRef(ref: string): Promise<WorkItem | null> {
  try {
    if (UUID_RE.test(ref)) {
      const res = await fetch(`/api/work-items/${encodeURIComponent(ref)}`, { credentials: "include" });
      return res.ok ? ((await res.json()) as WorkItem) : null;
    }
    const res = await fetch(`/api/work-items/resolve?identifiers=${encodeURIComponent(ref)}`, {
      credentials: "include",
    });
    if (!res.ok) return null;
    const rows = (await res.json()) as WorkItem[];
    return Array.isArray(rows) ? (rows[0] ?? null) : null;
  } catch (err) {
    captureUiError(err, { area: "Work", operation: "redirect_read_ticket", expected: true, severity: "warning" });
    return null;
  }
}

/** Where a ticket opens now: its Team's queue with the sheet on it. */
export function ticketHref(item: Pick<WorkItem, "id" | "deskId">): string | null {
  return item.deskId
    ? `/desks/${encodeURIComponent(item.deskId)}?item=${encodeURIComponent(item.id)}`
    : null;
}

function TicketRedirect({ workItemRef }: { workItemRef: string }) {
  const [to, setTo] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void readWorkItemRef(workItemRef).then((item) => {
      if (cancelled) return;
      const href = item ? ticketHref(item) : null;
      if (!href) toast.error("That ticket could not be opened.");
      setTo(href ?? "/waiting");
    });
    return () => {
      cancelled = true;
    };
  }, [workItemRef]);
  if (to) return <Navigate to={to} replace />;
  return (
    <div className="empty" role="status" data-testid="work-redirect-loading">
      <p>Opening the ticket.</p>
    </div>
  );
}

/** `/work`: a deep link resolves to its ticket, a review to Waiting on you, the rest is Waiting on you. */
export function WorkIndexRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const workItem = params.get("workItem");
  if (workItem) return <TicketRedirect workItemRef={workItem} />;
  const review = params.get("review");
  if (review) return <Navigate to={`/waiting?review=${encodeURIComponent(review)}`} replace />;
  // Work filtered to one Team is that Team's queue now.
  const team = params.get("team");
  if (team) return <Navigate to={`/desks/${encodeURIComponent(team)}`} replace />;
  return <Navigate to="/waiting" replace />;
}

/** `/work/:workItemId` and `/work/:workItemId/receipt`. */
export function WorkItemRedirect() {
  const { workItemId } = useParams<{ workItemId: string }>();
  if (!workItemId) return <Navigate to="/waiting" replace />;
  return <TicketRedirect workItemRef={workItemId} />;
}

/** `/work/add`: Add tickets is a sheet on a Team's queue now. */
export function WorkAddRedirect() {
  return <Navigate to="/waiting?add=1" replace />;
}
